import React, { useState } from 'react'
import { useTranslation } from 'react-i18next';
import { useFormik } from "formik"
import * as Yup from "yup"
import ReCAPTCHA from "react-google-recaptcha"

// Components, Icons
import TextAreaInput from 'components/input/TextAreaInput'
import TextInput from 'components/input/TextInput'
import EmailIcon from 'assets/icons/EmailIcon';
import WhatsappIcon from 'assets/icons/social-media/WhatsappIcon';
import Newsletter from 'components/layout/Newsletter'

// Apis
import useFetch from 'utils/hooks/useFetch'
import { SEND_CONTACT_FORM } from 'api';

function ContactUs() {
  const { t } = useTranslation();
  const { fetch: sendContactForm } = useFetch(SEND_CONTACT_FORM)
  const [isSubmitted, setIsSubmitted] = useState(false)
  const [isErrorSubmit, setIsErrorSubmit] = useState(false)

  const formik = useFormik({
    initialValues: {
      email_address: '',
      full_name: '',
      company_name: '',
      job_title: '',
      message: '',
      is_captcha_success: false
    },
    validationSchema: Yup.object().shape({
      email_address: Yup.string().required('Please input your email address').email('Invalid email format'),
      full_name: Yup.string().required('Please input your full name'),
      company_name: Yup.string().required('Please input your company name'),
      job_title: Yup.string().required('Please input your job title'),
      message: Yup.string().required('Please input your message'),
      is_captcha_success: Yup.boolean().required('Please input captcha'),
    }),
    validateOnBlur: false,
    validateOnChange: false,
    onSubmit: (values) => {
      handleSubmitForm(values)
    },
  });

  const handleSubmitForm = async (values) => {
    const newContactFormSubmitted = await sendContactForm({
      data: {
        full_name: values.full_name,
        email_address: values.email_address,
        company_name: values.company_name,
        job_title: values.job_title,
        message: values.message
      }
    })
    if (newContactFormSubmitted && newContactFormSubmitted.success) {
      setIsSubmitted(true)
      setTimeout(() => {
        setIsSubmitted(false)
        window.location.reload()
      }, 1000);
    } else {
      setIsErrorSubmit(true)
      setTimeout(() => {
        setIsErrorSubmit(false)
      }, 2000);
    }
  }

  const handleCaptcha = () => {
    formik.setFieldValue('is_captcha_success', true)
  }

  return (
    <div>
      <section>
        <div className='max-w-xl px-4 py-4 mx-auto space-y-6 md:max-w-4xl lg:max-w-6xl sm:px-8 sm:py-8'>
          <div className="grid grid-cols-1 gap-16 sm:grid-cols-2">
            <div>
              <p className='pb-4 text-lg font-bold uppercase md:text-xl xl:text-2xl text-app-dark-blue'>Our Office</p>
              <p className='pb-4 text-2xl font-bold xl:text-4xl app-animation-translate-start text-app-orange'>
                ISA Academy<br />
              </p>
              <p className='pb-4 text-xl font-bold xl:text-2xl app-animation-translate-start text-app-orange'>
                PT Inhocsigno Hominum Salvator
              </p>
              <p className='pb-4 text-base md:text-lg xl:text-xl app-animation-translate-start'>
                <b>AKR Tower 16th Floor</b><br />
                Jl. Panjang No. 5 <br />
                Jakarta Barat - Daerah Khusus Ibukota Jakarta 11530<br />
              </p>
              <div className='flex flex-row items-center gap-3 text-base cursor-pointer md:text-lg xl:text-xl app-animation-translate-start' onClick={() => window.open('https://wa.me/6281218467661')}><WhatsappIcon /> (+62) 812 1846 7661</div> <br />
              <div className='flex flex-row items-center gap-3 text-base cursor-pointer md:text-lg xl:text-xl app-animation-translate-start' onClick={() => window.open("mailto:academy@sustainability.co.id")}><EmailIcon /> academy@sustainability.co.id</div>
            </div>
            <div className='space-y-4 app-animation-translate-start'>
              <div>
                <p className='text-xl font-bold md:text-2xl xl:text-4xl app-animation-translate-start text-app-orange'>Have inquiries?</p>
                <p className='text-xl font-bold md:text-2xl xl:text-4xl app-animation-translate-start text-app-orange'>Let's keep in touch.</p>
              </div>
              <div>
                <p className='text-lg font-bold uppercase md:text-xl xl:text-2xl text-app-dark-blue app-animation-translate-start'>Contact Us</p>
              </div>
              <div className='space-y-4 app-animation-scale-start'>
                <TextInput
                  labelClassname={'text-app-dark-blue text-base md:text-lg'}
                  name="full_name"
                  label={'Full Name'}
                  required
                  placeholder={'Enter your full name...'}
                  value={formik.values.full_name}
                  onChange={formik.handleChange}
                  error={formik.errors.full_name}
                />
                <TextInput
                  labelClassname={'text-app-dark-blue text-base md:text-lg'}
                  name="email_address"
                  label={'Email Address'}
                  required
                  placeholder={'Enter your email address...'}
                  value={formik.values.email_address}
                  onChange={formik.handleChange}
                  error={formik.errors.email_address}
                />
                <TextInput
                  labelClassname={'text-app-dark-blue text-base md:text-lg'}
                  name="company_name"
                  label={'Company Name'}
                  required
                  placeholder={'Enter your company name...'}
                  value={formik.values.company_name}
                  onChange={formik.handleChange}
                  error={formik.errors.company_name}
                />
                <TextInput
                  labelClassname={'text-app-dark-blue text-base md:text-lg'}
                  name="job_title"
                  label={'Job Title'}
                  required
                  placeholder={'Enter your job title...'}
                  value={formik.values.job_title}
                  onChange={formik.handleChange}
                  error={formik.errors.job_title}
                />
                <TextAreaInput
                  labelClassname={'text-app-dark-blue text-base md:text-lg'}
                  name="message"
                  label={'Message'}
                  required
                  placeholder={'Leave your message here...'}
                  rows={8}
                  value={formik.values.message}
                  onChange={formik.handleChange}
                  error={formik.errors.message}
                />
                <ReCAPTCHA
                  sitekey="6LdcHv4qAAAAAJeVholb9OTwc2UrBoOlFzpFJ45g"
                  onChange={handleCaptcha}
                />
                <button
                  onClick={formik.handleSubmit}
                  className='w-full px-12 py-2 text-base font-semibold text-white uppercase bg-black rounded-md md:text-lg disabled:opacity-50'
                  disabled={
                    !formik.values.email_address ||
                    formik.values.email_address === '' ||
                    !formik.values.full_name ||
                    formik.values.full_name === '' ||
                    !formik.values.company_name ||
                    formik.values.company_name === '' ||
                    !formik.values.job_title ||
                    formik.values.job_title === '' ||
                    !formik.values.message ||
                    formik.values.message === ''
                  }
                >
                  Submit
                </button>
                {isSubmitted && (
                  <p className='text-xs text-center text-app-light-blue md:text-sm' >Your inquiry has successfully been submitted</p>
                )}
                {isErrorSubmit && (
                  <p className='text-xs text-center text-red-500 md:text-sm' >There is something wrong when processing the request</p>
                )}
              </div>
            </div>
          </div>
        </div >
      </section >

      {/* <Newsletter /> */}
    </div >
  )
}

export default ContactUs
